import { ErrorMessage } from '@hookform/error-message'
import { Checkbox, Col, Flex, Form, Input, Row, Select, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { Controller, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import CustomSelect from 'src/app/shared/components/common/select'
import { getIssuedCheckFileFormats, getPayeeMatchParameters } from 'src/app/shared/services/ClientSetupService'
import { getLookupValues } from 'src/app/store/masterSetup'
import { colorBorderDisabled } from 'src/app/themes/primaryThemeDefault'
import { getCutoffTimeList } from './utils'

interface CheckExceptionsProps {
  control: any
  isDisplayIssuedPayee: boolean
  isCreateExceptions: boolean
  isPayeeMatchChecked: boolean
  autoLoadIssuedCheckFiles: boolean
  errors: any
  setValue: UseFormSetValue<any>
  dirtyFields: any
  initialValues: any
}

interface IPayeeMatchParameter {
  id: string
  isDefault: boolean
  name: string
  payeeMatch: boolean
}

function CheckExceptions(props: CheckExceptionsProps) {
  const [issuedCheckFileFormats, setIssuedCheckFileFormats] = useState<any>([])
  const [fetchingIssuedCheckFile, setIsfetchingIssuedCheckFile] = useState<boolean>(false)
  const [payeeMatchParameters, setPayeeMatchParameters] = useState<IPayeeMatchParameter[]>([])
  const [fetchingPayeeMatchParameters, setIsfetchingPayeeMatchParameters] = useState<boolean>(false)
  const [changedCreateException, setChangedCreateException] = useState(false)

  const {
    control,
    isDisplayIssuedPayee,
    isCreateExceptions,
    autoLoadIssuedCheckFiles,
    isPayeeMatchChecked,
    errors,
    setValue,
    dirtyFields,
    initialValues,
  } = props
  const lookupValues = useSelector(getLookupValues)
  const { t } = useTranslation()

  const defaultCreateException = initialValues?.defaultCheckExceptionCreationType !== 'NOCREATE'

  const getPayeeMatchParametersList = async () => {
    try {
      setIsfetchingPayeeMatchParameters(true)
      const res = await getPayeeMatchParameters()
      if (res?.data?.length) setPayeeMatchParameters(res?.data)
    } finally {
      setIsfetchingPayeeMatchParameters(false)
    }
  }

  const getIssuedCheckFileFormatsList = async () => {
    try {
      setIsfetchingIssuedCheckFile(true)
      const res = await getIssuedCheckFileFormats()
      if (res?.data?.length) setIssuedCheckFileFormats(res?.data)
    } finally {
      setIsfetchingIssuedCheckFile(false)
    }
  }

  const defaultPayeeMatch = payeeMatchParameters?.find((item: IPayeeMatchParameter) => item?.isDefault)

  useEffect(() => {
    getIssuedCheckFileFormatsList()
    getPayeeMatchParametersList()
  }, [])

  const cutoffTimeList = getCutoffTimeList(15)

  const { DefaultCheckExceptionCreationType, DefaultDecision, DefaultReason } = lookupValues || {}

  useEffect(() => {
    if (!defaultCreateException && isCreateExceptions) {
      setValue('payeeMatch', true)
      return
    }

    if (!isCreateExceptions) setChangedCreateException(true)

    if (isCreateExceptions && changedCreateException) setValue('payeeMatch', true)
  }, [isCreateExceptions, setValue, dirtyFields, defaultCreateException, changedCreateException])

  return (
    <Flex vertical style={{ paddingBottom: '24px' }}>
      <Typography.Text style={{ fontSize: '18px', fontWeight: '700', lineHeight: '28px', paddingBottom: '4px' }}>
        {t('ClientSetup:addNewClient.checkExceptions')}
      </Typography.Text>
      <Flex vertical style={{ border: `1px solid ${colorBorderDisabled}`, padding: '16px', borderRadius: '16px' }}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label={t('ClientSetup:addNewClient.label.checkExceptionCreationType')} className='isRequired'>
              <Controller
                name='defaultCheckExceptionCreationType'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={DefaultCheckExceptionCreationType}
                    onChange={(value) => {
                      if (value === 'CREATE') setValue('payeeMatchParameterId', defaultPayeeMatch?.id)
                      else setValue('payeeMatchParameterId', null)
                      field?.onChange(value)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='defaultCheckExceptionCreationType' />
              </Typography>
            </Form.Item>

            {isCreateExceptions && (
              <Form.Item label={t('ClientSetup:addNewClient.label.checkDecision')} className='isRequired'>
                <Controller
                  name='defaultCheckDecision'
                  control={control}
                  render={({ field }) => <Select {...field} options={DefaultDecision} />}
                />
              </Form.Item>
            )}
          </Col>

          {isCreateExceptions && (
            <>
              <Col span={12}>
                <Form.Item label={t('ClientSetup:addNewClient.label.checkExceptionCutoffTime')} className='isRequired'>
                  <Controller
                    name='defaultCheckExceptionCutoffTime'
                    control={control}
                    render={({ field }) => <Select {...field} options={cutoffTimeList} />}
                    disabled
                  />
                </Form.Item>

                <Form.Item label={t('ClientSetup:addNewClient.label.checkReason')} className='isRequired'>
                  <Controller
                    name='defaultCheckReason'
                    control={control}
                    render={({ field }) => <Select {...field} options={DefaultReason} />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('ClientSetup:addNewClient.label.issuedCheckFileFormats')}>
                  <Controller
                    name='issuedCheckFileFormats'
                    control={control}
                    render={({ field, fieldState }) => (
                      <CustomSelect
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        mode='multiple'
                        placeholder={t('ClientSetup:addNewClient.placeholder.selectIssuedCheckFileFormats')}
                        options={issuedCheckFileFormats?.map((item: any) => ({
                          ...item,
                          value: item?.id,
                          label: item?.name,
                        }))}
                        showSearch
                        maxTagCount={'responsive'}
                        maxTagPlaceholder={(omittedValues) => (
                          <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                            <span>+ {omittedValues.length}...</span>
                          </Tooltip>
                        )}
                        loading={fetchingIssuedCheckFile}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='issuedCheckFileFormats' />
                  </Typography>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        {isCreateExceptions && (
          <>
            <Row gutter={10} align='middle'>
              <Col span={12}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='displayIssuedPayee'
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field.value}>
                        {t('ClientSetup:addNewClient.label.displayIssuedPayee')}
                      </Checkbox>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='payeeMatch'
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(value) => {
                          field.onChange(value)
                          if (value) {
                            setValue('payeeMatchParameterId', defaultPayeeMatch?.id)
                          }
                        }}
                      >
                        {t('ClientSetup:addNewClient.label.payeeMatch')}
                      </Checkbox>
                    )}
                    disabled={!isDisplayIssuedPayee}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Controller
                    name='payeeMatchParameterId'
                    control={control}
                    render={({ field, fieldState }) => (
                      <Select
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        value={field?.value || undefined}
                        placeholder={t('ClientSetup:addNewClient.placeholder.payeeMatchParameter')}
                        options={payeeMatchParameters?.map((item) => ({
                          ...item,
                          value: item?.id,
                          label: item?.name,
                        }))}
                        loading={fetchingPayeeMatchParameters}
                      />
                    )}
                    disabled={!isDisplayIssuedPayee || !isPayeeMatchChecked}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='payeeMatchParameterId' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10} align='middle'>
              <Col span={12}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='displayNotes'
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field.value}>
                        {t('ClientSetup:addNewClient.label.displayNotes')}
                      </Checkbox>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='autoLoadIssuedCheckFiles'
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field.value}>
                        {t('ClientSetup:addNewClient.label.autoLoadIssuedCheckFiles')}
                      </Checkbox>
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item>
                  <Controller
                    name='sftpFileLocation'
                    control={control}
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        placeholder={t('ClientSetup:addNewClient.placeholder.enterSFTPFileLocation')}
                        maxLength={255}
                        onBlur={(e) => {
                          field.onChange(e?.target?.value?.trim())
                        }}
                      />
                    )}
                    disabled={!autoLoadIssuedCheckFiles}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='sftpFileLocation' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default CheckExceptions
